import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Modal, LinearProgress, Button } from '@material-ui/core';
import defaultStyles from './AsnDisplay.module.css';
import jlrStyles from './AsnDisplayJlr.module.css';
import { AttributesVirtualizedTable } from '..';
import TodayIcon from '@material-ui/icons/Today';
import { getDatesForRange, setStylesAccordingToPlant } from '../../utils/utils';
import { API_RESOURCE_URLS, DATE_FORMAT, JLR_PLANT } from '../../constants';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { API } from '../../apis/api';
import LineDropdownSelector from '../LineDropdownSelector/LineDropdownSelector';
import { useAuthorizationContext } from '../AuthorizationProvider/AuthorizationProvider';
import { buildErrorMessage } from '../../apis/calls';
import { formatDataToIncludeColour } from '../../utils/tableUtils';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';

const today = moment();

const AsnDisplay = ({
  enableLineSelection,
  open,
  handleClose,
  startDate = today,
  endDate,
  plant,
  line,
  lineName,
}) => {
  const { user } = useAuthorizationContext();
  const { showInternalError } = usePopupManager();
  const [activeDate, setActiveDate] = useState('');
  const [selectedDates, setSelectedDates] = useState([]);
  const [asnData, setAsnData] = useState({
    loading: false,
    data: [],
  });
  const [styles, setStyles] = useState(defaultStyles);
  const [selectedPlant] = usePlantAndLinesStore().plantState;

  useEffect(() => {
    setStylesAccordingToPlant(
      selectedPlant,
      setStyles,
      defaultStyles,
      jlrStyles,
    );
  }, [selectedPlant]);

  const columns = [
    {
      title: 'ASN',
      field: 'asn',
      width: 120,
      style: {
        textAlign: 'left',
      },
    },
    {
      title: 'VC',
      field: 'vc',
      width: 120,
      style: {
        textAlign: 'left',
      },
    },
    {
      title: 'Description',
      field: 'vcDescription',
      width: 280,
      style: {
        textAlign: 'left',
      },
    },
    {
      width: 160,
      title: 'Planned FDT',
      field: 'plannedDropTimestamp',
      render: row =>
        row.plannedDropTimestamp
          ? moment(row.plannedDropTimestamp).format('hh:mm A DD/MM/YYYY')
          : null,
      enableSearch: true,
      enableFilter: true,
    },
  ];

  const getAsnOfLineForDate = async dateString => {
    const response = await API.get(API_RESOURCE_URLS.VC_ASN, {
      params: {
        from: dateString,
        to: dateString,
        line,
        plant,
        status: user.viewableChassisDropStatuses,
      },
    });
    const data = formatDataToIncludeColour(response.data);
    return data;
  };

  const sortData = asnData => {
    asnData.sort((a, b) => Number(a.serialNumber) - Number(b.serialNumber));
    return asnData;
  };

  const loadAsnsForDate = async date => {
    if (!date) return setAsnData({ loading: false, data: [] });
    try {
      setAsnData(prevState => ({ ...prevState, loading: true }));
      const dateString = date.format(DATE_FORMAT.ISO);
      const data = await getAsnOfLineForDate(dateString);
      const sortedData = sortData(data);
      setAsnData({ loading: false, data: sortedData });
    } catch (error) {
      console.error('failed to load asn data in view asn');
      console.error(buildErrorMessage(error));
      setAsnData(prevState => ({ ...prevState, loading: false }));
      handleClose();
      showInternalError();
    }
  };

  useEffect(() => {
    loadAsnsForDate(activeDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDate]);

  useEffect(() => {
    if (open) {
      if (!endDate || !startDate || endDate.isBefore(startDate, 'day')) {
        setSelectedDates([]);
        setActiveDate(null);
      } else {
        const dates = getDatesForRange(startDate, endDate);
        setSelectedDates(dates);
        setActiveDate(dates[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, endDate, startDate, line]);

  const renderTableView = () => {
    return (
      <>
        <div className={styles.header}>
          <div className={styles.lineName}>{lineName}</div>
          <div className={styles.title}>
            <span style={{ display: 'block' }}>ASN</span>
            <span style={{ display: 'block', fontWeight: '600' }}>
              Sequence
            </span>
          </div>

          <div className={styles.calicon}>
            <span>For</span>
            <TodayIcon />
          </div>
          <div className={styles.datesSelectorBar}>
            <span className={styles.monthName}>
              {activeDate && activeDate.format('MMMM')}
            </span>
            <div className={styles.hrScroll}>
              <div className={styles.datesRow}>
                {selectedDates.length ? (
                  selectedDates.map(date => (
                    <button
                      className={
                        date === activeDate
                          ? styles.activeDateButton
                          : styles.datesSelectorButton
                      }
                      onClick={() => setActiveDate(date)}
                      key={date}
                    >
                      {date.format('DD')}
                    </button>
                  ))
                ) : (
                  <div className={styles.notAvailableMsg}>
                    No ASNs available
                  </div>
                )}
              </div>
            </div>
          </div>
          {enableLineSelection && (
            <div className={styles.selLine}>
              <span className={styles.selLineText}>Select a Line</span>
              <LineDropdownSelector />
            </div>
          )}
        </div>
        <div className={styles.mainTableContainer}>
          <div className={styles.tableContainer}>
            <AttributesVirtualizedTable
              fixedColumns={columns}
              rowsData={asnData}
              maxEmptyRowPadding={9}
              fixedColumnsStyles={
                selectedPlant.label === JLR_PLANT
                  ? {
                      headerStyles: {
                        color: '#26662E',
                        fontSize: '13px',
                        fontWeight: '600',
                        boxSizing: 'border-box',
                      },
                    }
                  : {
                      headerStyles: {
                        color: '#0275A7',
                        fontSize: '13px',
                        fontWeight: '600',
                        boxSizing: 'border-box',
                      },
                    }
              }
              optionalColumnsStyles={
                selectedPlant.label === JLR_PLANT
                  ? {
                      headerStyles: {
                        color: '#26662E',
                        backgroundColor: 'white',
                        zIndex: 1,
                        boxSizing: 'border-box',
                      },
                    }
                  : {
                      headerStyles: {
                        color: '#0275a7',
                        backgroundColor: 'white',
                        zIndex: 1,
                        boxSizing: 'border-box',
                      },
                    }
              }
            />
          </div>
        </div>

        <div className={styles.footer}>
          <Button
            className={styles.footerButton}
            variant="contained"
            onClick={handleClose}
          >
            OK
          </Button>
        </div>
      </>
    );
  };
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="heijunka-sequence-modal"
      aria-describedby="modal to show heijunka sequence"
    >
      <div className={styles.container}>
        {asnData.loading ? <LinearProgress /> : renderTableView()}
      </div>
    </Modal>
  );
};

AsnDisplay.propTypes = {
  enableLineSelection: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  startDate: PropTypes.objectOf(moment),
  endDate: PropTypes.objectOf(moment),
  plant: PropTypes.string.isRequired,
  line: PropTypes.string.isRequired,
  lineName: PropTypes.string.isRequired,
};

export default AsnDisplay;
